import BemClass from '@upsales/components/Utils/bemClass';
import React, { useState, useEffect, ComponentProps, useRef } from 'react';
import './AppSidebar.scss';
import { Block, Card, Icon, IconName, Link, OutsideClick, Text, ClickableItem, Tooltip } from '@upsales/components';
import { CancelablePromise, makeCancelable } from '@upsales/components/Utils/CancelablePromise';
import Links from 'App/resources/Links';
import logError from 'Helpers/logError';
import { RootState } from 'Store/index';
import history from 'App/pages/routes/history';
import { useAppDispatch, useFeatureAvailable, useSelector, useSoftDeployAccess } from '../hooks';
import { useTranslation } from 'Components/Helpers/translate';
import LinkType from 'App/resources/Model/Links';
import { Feature, getFeatureAvailableFromState, getSoftDeployAccessFromState } from '../hooks/featureHelper';
import { globalTracker } from 'App/babel/helpers/Tracker';
import { openDrawer } from 'Services/Drawer';
import openModal, { shouldOpenModal } from 'App/services/Modal';
import InAppChat from 'Services/InAppChat';
import { SidebarSavedModalButton, SidebarSavedLocalStorageModalButton } from '../SidebarSavedModalButton';
import { setAIChatOpen, setSidebarLinks } from 'Store/actions/AppActions';
import { AppState } from 'Store/reducers/AppReducer';
import useCleanMyDataNotification from 'App/components/CleanMyData/useCleanMyDataNotification';
import { openEditAppointment } from 'Components/Modals/Appointment/EditAppointment';
import { openEditCampaignModal } from 'App/components/EditCampaign/EditCampaign';
import config from 'App/babel/config';
import { openEditOrder } from '../EditOrder';

const useAddActions = (): { title: string; type: string; show: boolean; icon?: IconName; do: () => void }[] => {
	const hasActivities = useFeatureAvailable(Feature.ACTIVITIES_AND_APPOINTMENTS);
	const hasTodoList = useSoftDeployAccess('TODO_LIST') && hasActivities;
	const { metadata, self, accessRights, admin, customerId, accountSelf } = useSelector(({ App }) => ({
		metadata: App.metadata,
		self: App.self,
		accessRights: App.accessRights,
		admin: App.self?.administrator ?? false,
		customerId: App.customerId,
		accountSelf: App.accountSelf
	}));

	const isCRMUser = self?.crm ?? true;

	const showEmail =
		useFeatureAvailable(Feature.EMAIL) &&
		(admin || metadata?.map.mailActivated || !!metadata?.integrations.inits.mail?.length || false);

	const showGroupMailAndMailTemplate =
		useFeatureAvailable(Feature.GROUP_MAIL) && (admin || metadata?.map.mailActivated || false);

	const hasCustomerSupport = useFeatureAvailable(Feature.CUSTOMER_SUPPORT);
	const showSupportTicket = !!self?.support && hasCustomerSupport;

	const hasProjectPlan = useSoftDeployAccess('PROJECT_PLAN');
	const hasProjectPlanFeature = useFeatureAvailable(Feature.PROJECT_PLAN);
	const requireServiceAddon = useSoftDeployAccess('SERVICE_MANAGEMENT_REQUIRE_ADDON');

	const showProjectPlan = !!(
		(hasProjectPlan || hasProjectPlanFeature) &&
		(requireServiceAddon ? self?.service : self?.service || self?.crm)
	);

	const showActivity =
		isCRMUser &&
		getFeatureAvailableFromState(accountSelf, Feature.ACTIVITIES_AND_APPOINTMENTS) &&
		!(getSoftDeployAccessFromState(accountSelf, 'REMOVE_ACTIVITIES') && hasTodoList);
	const showTodo = isCRMUser && hasTodoList;
	const showPhoneCall = isCRMUser && hasTodoList;
	const showAppointment = isCRMUser && getFeatureAvailableFromState(accountSelf, Feature.ACTIVITIES_AND_APPOINTMENTS);

	return [
		{
			title: 'default.account',
			type: 'account',
			show: getFeatureAvailableFromState(accountSelf, Feature.COMPANIES_AND_CONTACTS),
			icon: 'building-o',
			do: () => {
				if (shouldOpenModal('CreateAccount')) {
					openModal('CreateAccount');
				} else {
					Tools.$upModal.open('createAccount', { customerId });
				}
			}
		},
		{
			title: 'default.contact',
			type: 'contact',
			show: getFeatureAvailableFromState(accountSelf, Feature.COMPANIES_AND_CONTACTS),
			icon: 'user',
			do: () => {
				Tools.$upModal.open('editContact', { customerId });
			}
		},
		{
			title: 'default.activity',
			type: 'activity',
			show: showActivity,
			icon: 'activity',
			do: () => {
				Tools.$upModal.open('editActivity', {});
			}
		},
		{
			title: 'todo.createTodo',
			type: 'todo',
			show: showTodo,
			icon: 'activity',
			do: () => {
				openDrawer('CreateTodo');
			}
		},
		{
			title: 'todo.planACall',
			type: 'phonecall',
			show: showPhoneCall,
			icon: 'phone',
			do: () => {
				openDrawer('CreateCall');
			}
		},
		{
			title: 'todo.bookAppointment',
			type: 'appointment',
			show: showAppointment,
			icon: 'calendar',
			do: () => {
				openEditAppointment({});
			}
		},
		{
			title: 'default.opportunity',
			type: 'opportunity',
			show: getFeatureAvailableFromState(accountSelf, Feature.PIPELINE),
			icon: 'opportunity',
			do: () => {
				openEditOrder({ type: 'opportunity' });
			}
		},
		{
			title: 'default.order',
			type: 'order',
			show: getFeatureAvailableFromState(accountSelf, Feature.ORDERS),
			icon: 'dollar',
			do: () => {
				openEditOrder({ type: 'order' });
			}
		},
		{
			title: 'default.agreement',
			type: 'agreement',
			show:
				!!metadata?.params.AgreementEnabled &&
				getFeatureAvailableFromState(accountSelf, Feature.RECURRING_ORDER) &&
				accessRights.Agreement,
			icon: 'recurring-order',
			do: () => {
				if (getSoftDeployAccessFromState(accountSelf, 'SUBSCRIPTION_MODAL')) {
					openModal('CreateSubscription', { createdFrom: 'sidebar', dontWait: true });
				} else {
					Tools.$upModal.open('editAgreement', { customerId });
				}
			}
		},
		{
			title: 'default.email',
			type: 'mail',
			show: showEmail,
			icon: 'envelope',
			do: () => {
				openDrawer('NewSingleMail');
			}
		},
		{
			title: 'default.mailinglist',
			type: 'mailCampaign',
			show: showGroupMailAndMailTemplate && (admin || self?.userParams.mailAdmin || false),
			icon: 'email-campaign',
			do: () => {
				if (Tools.FeatureHelper.hasSoftDeployAccess('REACT_CREATE_GROUP_MAIL')) {
					openModal('CreateGroupMail');
				} else {
					Tools.$upModal.open('createGroupMail');
				}
			}
		},
		{
			title: 'mail.template',
			type: 'mailTemplate',
			show: showGroupMailAndMailTemplate,
			icon: 'envelope',
			do: () => {
				openModal('CreateMailTemplate');
			}
		},
		{
			title: 'default.campaign',
			type: 'campaign',
			show: getFeatureAvailableFromState(accountSelf, Feature.PROJECTS),
			icon: 'bullhorn',
			do: () => {
				openEditCampaignModal({ customerId });
			}
		},
		{
			title: 'form.form',
			type: 'form',
			show: getFeatureAvailableFromState(accountSelf, Feature.FORMS),
			icon: 'form',
			do: () => {
				openModal('CreateForm', { formType: 'form' });
			}
		},
		{
			title: 'form.landingPage',
			type: 'landingpage',
			show: getFeatureAvailableFromState(accountSelf, Feature.FORMS),
			icon: 'file-text',
			do: () => {
				openModal('CreateForm', { formType: 'landing' });
			}
		},
		{
			title: 'utm.generator',
			type: 'utm',
			show: getFeatureAvailableFromState(accountSelf, Feature.UTM_GENERATOR),
			icon: 'link',
			do: () => {
				openDrawer('UtmLinkGenerator');
			}
		},
		{
			title: 'ticket.createTicket',
			type: 'support',
			show: showSupportTicket,
			icon: 'customer-support',
			do: () => {
				openDrawer('EditTicket');
			}
		},
		{
			title: 'projectPlan.createProjectPlan',
			type: 'projectPlan',
			show: showProjectPlan,
			icon: 'project',
			do: () => {
				openDrawer('EditProjectPlan');
			}
		}
	];
};

const SidebarButton = ({
	active,
	notification,
	icon,
	loading = false,
	renderDropdown,
	onClick,
	tooltip
}: {
	icon: ComponentProps<typeof Icon>['name'];
	loading?: boolean;
	active?: boolean;
	notification?: boolean;
	renderDropdown?: (close: () => void) => JSX.Element;
	onClick?: () => void;
	tooltip: string;
}) => {
	const [isOpen, setIsOpen] = useState(false);
	const clickedOutside = useRef(false);
	const { t } = useTranslation();
	const doClick = () => {
		onClick?.();
		if (renderDropdown) {
			if (!clickedOutside.current && !isOpen) {
				setIsOpen(true);
			} else if (clickedOutside.current && !isOpen) {
				clickedOutside.current = false;
			} else {
				clickedOutside.current = false;
				setIsOpen(true);
			}
		}
	};
	const doOutsideClick = () => {
		if (isOpen) {
			clickedOutside.current = true;
			setIsOpen(false);
		} else {
			clickedOutside.current = false;
		}
	};
	const classes = new BemClass('AppSidebar__button-wrap').mod({ active });

	return (
		<div className={classes.b()}>
			<Tooltip title={t(tooltip)} position="right" distance={24} disabled={loading}>
				<ClickableItem
					block
					icon={icon}
					borderRadius
					color="grey-1"
					onClick={() => doClick()}
					loading={loading}
					active={isOpen}
				/>
				{active ? <Icon name="exclamation" className={classes.elem('active-icon').b()} /> : null}
				{notification ? <Icon color="white" name="exclamation" className={'notification-badge'} /> : null}
			</Tooltip>
			{renderDropdown ? (
				<OutsideClick outsideClick={doOutsideClick} targetClass="AppSidebar__dropdown">
					<dialog className={new BemClass('AppSidebar__dropdown').b()} open={isOpen}>
						<Card>{renderDropdown(() => setIsOpen(false))}</Card>
					</dialog>
				</OutsideClick>
			) : null}
		</div>
	);
};

const openChat = function (accountSelf: AppState['accountSelf']) {
	if (!InAppChat.isOpened()) {
		InAppChat.open();
	}
};

const AppSidebar = () => {
	const classes = new BemClass('AppSidebar');
	const { t } = useTranslation();
	const [loadingLinks, setLoadingLinks] = useState(true);
	const dispatch = useAppDispatch();
	const [chatUnread, setChatUnread] = useState(0);
	const [aiChatUnread, setAiChatUnread] = useState(0);
	const displayCalendar = Tools.FeatureHelper.isAvailable(Tools.FeatureHelper.Feature.ACTIVITIES_AND_APPOINTMENTS);
	const hasAIChat = useSoftDeployAccess('AI_CHAT');
	const hasHelp = (window as any)._elev !== undefined;
	const linksPromise = useRef<CancelablePromise<{ data: LinkType[] }> | null>(null);
	const { self, links, accountSelf, aichatOpen } = useSelector(({ App }: RootState) => ({
		self: App.self,
		links: App.sidebarLinks,
		accountSelf: App.accountSelf,
		aichatOpen: App.aichatOpen
	}));
	const [shouldShowCleanMyDataNotificationBadge, openCleaningModal] = useCleanMyDataNotification(self);

	const addActions = useAddActions();
	const hasChat = InAppChat.isEnabledAndLoaded();

	const isAdmin = self?.administrator;
	const hasWorkflows = useSoftDeployAccess('WORKFLOWS');
	const hasAI = useFeatureAvailable(Feature.AI);
	const hasAgentUserAccess = self?.userParams.agentUserAccess;
	const showAIAgent = (isAdmin || hasAgentUserAccess) && config.WORKFLOW_URL && hasWorkflows && hasAI;

	useEffect(() => {
		let unregisterChatCount: () => void;

		if (self) {
			// Get links
			// TODO: Put in redux so we can get updated links on add/remove
			linksPromise.current = makeCancelable(Links.find('sidebar', self.id));

			linksPromise.current.promise
				.then(res => {
					dispatch(setSidebarLinks(res.data));
					setLoadingLinks(false);
				})
				.catch(e => logError(e, 'Failed to load links'));

			if (hasChat) {
				unregisterChatCount = InAppChat.on('unreadCount', function (unreadCount: number) {
					setChatUnread(unreadCount);
				});
			}

			if (hasAIChat) {
				// Listen for AI Chat unread messages
				const aiChatUnreadListener = Tools.$rootScope.$on(
					'AIChat.unreadCount',
					(_ignore: any, count: number) => {
						// Defer state update to avoid updating during render
						setTimeout(() => {
							setAiChatUnread(count);
						}, 0);
					}
				);

				// Also listen for new messages to increment count
				const aiChatMessageListener = Tools.$rootScope.$on(
					'userPrivate.aiChatStreamChunk',
					(_ignore: any, data: { chatId?: string }) => {
						// Increment if message is for a different chat (we'll track current chat separately)
						if (data.chatId) {
							// Defer state update to avoid updating during render
							setTimeout(() => {
								setAiChatUnread(prev => prev + 1);
							}, 0);
						}
					}
				);

				return () => {
					aiChatUnreadListener();
					aiChatMessageListener();
				};
			}
		}
		return () => {
			linksPromise.current?.cancel();
			unregisterChatCount?.();
		};
	}, [hasAIChat, hasChat]);

	return (
		<div className={classes.b()}>
			<SidebarButton
				icon="plus"
				loading={loadingLinks}
				tooltip="sidebar.tooltip.add"
				renderDropdown={close => {
					return (
						<div className={classes.elem('add-dropdown').b()}>
							<Block space="mtl mrl mll mbl">
								<Text bold size="sm">
									{t('default.add')}
								</Text>
							</Block>
							{addActions.map(action =>
								action.show ? (
									<ClickableItem
										block
										icon={action.icon}
										key={action.type}
										onClick={() => {
											globalTracker.track('add', { type: action.type, location: 'sidebar' });
											action.do();
											close();
										}}
										title={t(action.title)}
									/>
								) : null
							)}
						</div>
					);
				}}
			/>
			{displayCalendar ? (
				<SidebarButton
					icon="calendar"
					loading={loadingLinks}
					tooltip="sidebar.tooltip.calendar"
					onClick={() => {
						history.push(`/calendar/?date=${new Date().toJSON()}&selected=workWeek`);
					}}
				/>
			) : null}
			{links.length ? (
				<SidebarButton
					icon="link"
					loading={loadingLinks}
					tooltip="sidebar.tooltip.dynamiclinks"
					renderDropdown={close => {
						return (
							<div className={classes.elem('dynamiclinks-dropdown').b()}>
								<Block space="mtl mrl mll mbl">
									<Text bold size="sm">
										{t('default.links')}
									</Text>
								</Block>
								{links.map(link => (
									<Link nostyle key={link.id} href={link.href} target={link.target}>
										<ClickableItem
											block
											renderLeft={() =>
												link.icon.includes('fa-') ? null : (
													<img className={classes.elem('link-img').b()} src={link.icon} />
												)
											}
											icon={
												link.icon.includes('fa-')
													? (link.icon.replace('fa-', '') as IconName)
													: undefined
											}
											iconStyle={{ color: '#' + link.color }}
											title={link.value}
											onClick={() => {
												close();
											}}
										/>
									</Link>
								))}
							</div>
						);
					}}
				/>
			) : null}
			{hasHelp ? (
				<SidebarButton
					icon="question"
					loading={loadingLinks}
					tooltip="sidebar.tooltip.help"
					onClick={() => window._elev.openModule?.('articles')}
				/>
			) : null}
			{hasChat ? (
				<SidebarButton
					icon="comment"
					active={chatUnread > 0}
					loading={loadingLinks}
					tooltip="sidebar.tooltip.chat"
					onClick={() => openChat(accountSelf)}
				/>
			) : null}
			{isAdmin ? (
				<SidebarButton
					data-testid="cleanMyDataButton"
					icon="broom"
					loading={loadingLinks}
					tooltip="sidebar.tooltip.cleanMyData"
					onClick={() => openCleaningModal()}
					notification={shouldShowCleanMyDataNotificationBadge}
				/>
			) : null}
			{showAIAgent ? (
				<SidebarButton
					data-testid="workflows"
					icon="ai-agent"
					tooltip="default.aiAgent"
					onClick={() => history.push('/ai-agent')}
				/>
			) : null}
			{hasAIChat ? (
				<SidebarButton
					data-testid="aiChatButton"
					icon="stars"
					tooltip="aiChat.title"
					loading={loadingLinks}
					notification={aiChatUnread > 0}
					onClick={() => {
						dispatch(setAIChatOpen(!aichatOpen));
						setAiChatUnread(0);
					}}
				/>
			) : null}
			{/*
				UIElements never worked in the old sidebar,
				so i will fix it in another ticket and spend some time to make it fit in properly
			 */}
			{/* <UiElements elements={uiElements} type="sidebar" /> */}
			<SidebarSavedModalButton />
			<SidebarSavedLocalStorageModalButton />
		</div>
	);
};

export default AppSidebar;
